import { css } from '@emotion/react';

import { typography2 as typography } from '@coursera/cds-core';

export const classes = {
  mediaDefault: 'cds-ChatContextAttachment-mediaDefault',
  mediaSmall: 'cds-ChatContextAttachment-mediaSmall',
  mediaLoading: 'cds-ChatContextAttachment-mediaLoading',
  mediaSlot: 'cds-ChatContextAttachment-mediaSlot',
  closeButton: 'cds-ChatContextAttachment-closeButton',
  metadataRoot: 'cds-ChatContextAttachment-metadataRoot',
  metadataDetails: 'cds-ChatContextAttachment-metadataDetails',
  metadataIconSlot: 'cds-ChatContextAttachment-metadataIconSlot',
  metadataLabel: 'cds-ChatContextAttachment-metadataLabel',
  metadataLoading: 'cds-ChatContextAttachment-metadataLoading',
  skeletonText: 'cds-ChatContextAttachment-skeletonText',
};

const getChatContextAttachmentCss = css`
  /* ── Media attachment (square thumbnail) ─────────────────────────────── */

  &.${classes.mediaDefault}, &.${classes.mediaSmall} {
    position: relative;
    border-radius: var(--cds-border-radius-100);
    overflow: hidden;
    flex-shrink: 0;
  }

  &.${classes.mediaDefault} {
    width: 56px;
    height: 56px;
    background: var(--cds-color-emphasis-neutral-background-default);
  }

  &.${classes.mediaSmall} {
    width: 32px;
    height: 32px;
    background: var(--cds-color-emphasis-neutral-background-default);
  }

  &.${classes.mediaLoading} {
    background: var(--cds-color-neutral-disabled-strong);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .${classes.mediaSlot} {
    position: absolute;
    inset: 0;

    /* Allow any child img/video to fill the slot as a cover thumbnail. */
    img,
    video {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  }

  /* ── Close button (used by both media and metadata) ──────────────────── */

  .${classes.closeButton} {
    position: absolute;
    inset-block-start: var(--cds-spacing-50);
    inset-inline-end: var(--cds-spacing-50);
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--cds-color-utility-overlay);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    cursor: pointer;
    color: var(--cds-color-neutral-primary-invert);

    &:focus-visible {
      outline: 2px solid var(--cds-color-interactive-stroke-primary-focus);
      outline-offset: 1px;
    }

    svg {
      width: 12px;
      height: 12px;
    }
  }

  /* ── Metadata attachment (pill) ──────────────────────────────────────── */

  &.${classes.metadataRoot} {
    display: inline-flex;
    align-items: center;
    gap: var(--cds-spacing-100);
    padding: var(--cds-spacing-100);
    background: var(--cds-color-neutral-background-primary);
    border-radius: var(--cds-border-radius-50);
    position: relative;
    flex-shrink: 0;

    .${classes.closeButton} {
      position: static;
      width: 16px;
      height: 16px;
      border-radius: 0;
      background: none;
      color: var(--cds-color-neutral-primary);
      padding: 0;

      &:hover {
        background: none;
        color: var(--cds-color-neutral-primary);
      }

      svg {
        width: 14px;
        height: 14px;
      }
    }
  }

  &.${classes.metadataLoading} {
    .${classes.metadataIconSlot} {
      background: var(--cds-color-emphasis-neutral-background-weak);
    }
  }

  .${classes.metadataDetails} {
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-100);
    flex-shrink: 0;
  }

  .${classes.metadataIconSlot} {
    width: 24px;
    height: 24px;
    border-radius: var(--cds-border-radius-100);
    background: var(--cds-color-emphasis-neutral-background-default);
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }

  .${classes.metadataLabel} {
    ${typography.bodySecondary};
    color: var(--cds-color-neutral-primary-weak);
    max-width: 136px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .${classes.skeletonText} {
    width: 40px;
    height: 16px;
    background: var(--cds-color-neutral-disabled);
    border-radius: var(--cds-border-radius-100);
    flex-shrink: 0;
  }
`;

export default getChatContextAttachmentCss;
